import { useTranslation } from "react-i18next";
/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import clsx from "clsx";

export const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"];

// The extension is read from the path only: a query or fragment can carry dots
// of its own (`photo.png?v=1.2`, a presigned URL's params).
export const isImageUrl = (url: string): boolean => {
  const path = url.split(/[?#]/)[0];
  return IMAGE_EXTS.includes(path.slice(path.lastIndexOf(".") + 1).toLowerCase());
};

/** The URL's last path segment when it names a file (signed URLs carry a query). */
const fileNameFromUrl = (url: string): string | null => {
  try {
    const name = decodeURIComponent(new URL(url, window.location.href).pathname.split("/").pop() ?? "");
    return name.includes(".") ? name : null;
  } catch {
    return null;
  }
};

/** White round control over the dark backdrop — one look for every action. */
const ROUND_BUTTON =
  "flex size-10 shrink-0 items-center justify-center rounded-full bg-background text-foreground shadow-lg transition hover:bg-muted";

type OpenPreviewFn = (images: string[], onClose?: () => void, startIndex?: number) => void;

const ImagePreviewContext = createContext<OpenPreviewFn>(() => {});

export const ImagePreviewProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { t } = useTranslation(["common"]);
  const [images, setImages] = useState<string[] | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [zoomed, setZoomed] = useState(false);
  const [visible, setVisible] = useState(false);
  const onCloseRef = useRef<(() => void) | undefined>(undefined);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOpen = Boolean(images && images.length > 0);

  const handleClose = useCallback(() => {
    setVisible(false);
    // Capture callback now — openPreview may overwrite onCloseRef before the timer fires
    const onCloseCb = onCloseRef.current;
    closeTimerRef.current = setTimeout(() => {
      setImages(null);
      onCloseCb?.();
    }, 200);
  }, []);

  const openPreview: OpenPreviewFn = useCallback((imgs, onClose, startIndex = 0) => {
    // Cancel any in-flight close animation so it can't wipe the new preview
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    onCloseRef.current = onClose;
    setImages(imgs);
    setCurrentIndex(Math.min(Math.max(startIndex, 0), Math.max(imgs.length - 1, 0)));
    setIsLoading(true);
    setZoomed(false);
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const goNext = useCallback(() => {
    if (!images) return;
    setIsLoading(true);
    setZoomed(false);
    setCurrentIndex((i) => (i + 1) % images.length);
  }, [images]);

  const goPrev = useCallback(() => {
    if (!images) return;
    setIsLoading(true);
    setZoomed(false);
    setCurrentIndex((i) => (i - 1 + images.length) % images.length);
  }, [images]);

  useEffect(() => {
    if (!isOpen) return;
    // Capture phase, and the keys stop here: the lightbox sits over panels
    // that close on Escape themselves (the Maya sidebar, drawers) — one
    // Escape should close the lightbox only.
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", handleKey, true);
    return () => window.removeEventListener("keydown", handleKey, true);
  }, [isOpen, handleClose, goNext, goPrev]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleDownload = async () => {
    const url = images?.[currentIndex];
    if (!url) return;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const ext =
        blob.type.split("/")[1] || url.split(".").pop()?.split("?")[0] || "jpg";
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = fileNameFromUrl(url) ?? `attachment-${currentIndex + 1}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      // noopener: preview URLs can come from untrusted content (AI replies),
      // and the opened page must not get a handle on this window.
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const count = images?.length ?? 0;
  const hasMultiple = count > 1;

  return (
    <ImagePreviewContext.Provider value={openPreview}>
      {children}
      {isOpen &&
        createPortal(
          // Outer container IS the backdrop — clicking anywhere on it closes the modal.
          // Individual content sections stop propagation so they don't trigger close.
          <div
            onClick={handleClose}
            className={clsx(
              // pointer-events-auto: a modal drawer underneath sets `pointer-events: none` on body.
              "pointer-events-auto fixed inset-0 z-9999 flex flex-col bg-black/85 backdrop-blur-xs transition-opacity duration-200",
              visible ? "opacity-100" : "opacity-0",
            )}
          >
            {/* Photo with its controls just above its top corner — clicks on
                the dark area around it close; clicks on the photo zoom. */}
            <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 py-4 sm:px-16">
              {hasMultiple && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goPrev();
                  }}
                  aria-label={t("common:buttons.previous")}
                  className={clsx(ROUND_BUTTON, "absolute start-3 z-10")}
                >
                  <ChevronLeft className="size-5 rtl-flip" />
                </button>
              )}

              <div
                onClick={(e) => e.stopPropagation()}
                className="flex max-h-full min-w-0 flex-col gap-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium tracking-wide text-white/80">
                    {hasMultiple
                      ? t("common:shared_ui.image_position", {
                          current: currentIndex + 1,
                          total: count,
                        })
                      : null}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setZoomed((z) => !z)}
                      className={ROUND_BUTTON}
                      title={zoomed ? t("common:buttons.zoom_out") : t("common:buttons.zoom_in")}
                      aria-label={zoomed ? t("common:buttons.zoom_out") : t("common:buttons.zoom_in")}
                    >
                      {zoomed ? <ZoomOut className="size-5" /> : <ZoomIn className="size-5" />}
                    </button>
                    <button
                      onClick={handleDownload}
                      className={ROUND_BUTTON}
                      title={t("common:buttons.download")}
                      aria-label={t("common:buttons.download")}
                    >
                      <Download className="size-5" />
                    </button>
                    <button
                      onClick={handleClose}
                      className={ROUND_BUTTON}
                      title={t("common:buttons.close")}
                      aria-label={t("common:buttons.close")}
                    >
                      <X className="size-5" />
                    </button>
                  </div>
                </div>

                <div className="relative flex min-h-0 items-center justify-center overflow-hidden rounded-md">
                  {isLoading && (
                    <div className="absolute inset-0 flex min-h-40 min-w-40 items-center justify-center">
                      <div className="size-10 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                    </div>
                  )}
                  <img
                    key={images![currentIndex]}
                    src={images![currentIndex]}
                    alt={`attachment ${currentIndex + 1}`}
                    onLoad={() => setIsLoading(false)}
                    onError={() => setIsLoading(false)}
                    onClick={() => setZoomed((z) => !z)}
                    className={clsx(
                      "max-w-full object-contain select-none transition-all duration-300",
                      isLoading ? "opacity-0" : "opacity-100",
                      zoomed ? "scale-150 cursor-zoom-out" : "scale-100 cursor-zoom-in",
                    )}
                    style={{ maxHeight: hasMultiple ? "calc(100vh - 200px)" : "calc(100vh - 120px)" }}
                    draggable={false}
                  />
                </div>
              </div>

              {hasMultiple && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goNext();
                  }}
                  aria-label={t("common:buttons.next")}
                  className={clsx(ROUND_BUTTON, "absolute end-3 z-10")}
                >
                  <ChevronRight className="size-5 rtl-flip" />
                </button>
              )}
            </div>

            {/* Thumbnail strip — stop propagation so clicking here doesn't close */}
            {hasMultiple && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="flex items-center justify-center gap-2 px-5 py-3 shrink-0"
              >
                {images!.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setCurrentIndex(i);
                      setIsLoading(true);
                      setZoomed(false);
                    }}
                    className={clsx(
                      "w-14 h-10 rounded overflow-hidden border-2 transition-all duration-150 shrink-0",
                      i === currentIndex
                        ? "border-white opacity-100 scale-110"
                        : "border-white/20 opacity-50 hover:opacity-80",
                    )}
                  >
                    <img
                      src={src}
                      alt={`thumb ${i + 1}`}
                      className="w-full h-full object-cover"
                      draggable={false}
                    />
                  </button>
                ))}
              </div>
            )}

            <div className="h-3 shrink-0" />
          </div>,
          document.body,
        )}
    </ImagePreviewContext.Provider>
  );
};

export const useImagePreview = () => useContext(ImagePreviewContext);

/**
 * useImageLinkPreview — click handler for a link or thumbnail whose target is
 * an image. A plain click opens the lightbox at `startIndex`; a modified click
 * (Cmd/Ctrl/Shift/Alt — new tab, new window, download) is left to the browser.
 */
export const useImageLinkPreview = () => {
  const openPreview = useImagePreview();
  return useCallback(
    (e: MouseEvent, images: string[], startIndex = 0) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      openPreview(images, undefined, startIndex);
    },
    [openPreview],
  );
};

/**
 * useFilePreview — handles any file URL.
 * Images open in the lightbox; non-images open in a new tab.
 * Accepts a single URL or an array (previews all if first URL is an image).
 */
export const useFilePreview = () => {
  const openPreview = useImagePreview();
  return useCallback(
    (urls: string | string[], onClose?: () => void) => {
      const list = (Array.isArray(urls) ? urls : [urls]).filter(
        Boolean,
      ) as string[];
      if (!list.length) return;
      if (isImageUrl(list[0])) {
        openPreview(list, onClose);
      } else {
        window.open(list[0], "_blank", "noopener,noreferrer");
      }
    },
    [openPreview],
  );
};
